import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Button,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, alpha, useTheme
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { searchShadowMinistries } from '../services/api';
import useTableSort from '../hooks/useTableSort';

// Raw sort keys for the sortable shadow-ministry-list columns.
const SHADOW_SORT_KEYS = {
  '#': (m) => Number(m.SMID) || 0,
  'Shadow Ministry': (m) => (m.Name || '').toLowerCase(),
  Appointed: (m) => m.DateStart || '',
  'End Date': (m) => m.DateEnd || '',
  Duration: (m) => {
    if (!m.DateStart) return 0;
    const end = m.DateEnd ? new Date(m.DateEnd) : new Date();
    return end - new Date(m.DateStart);
  },
};

export default function ShadowMinistriesSearch() {
  const theme = useTheme();
  const [shadowMinistries, setShadowMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShadowMinistries = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await searchShadowMinistries('*', 1, 100);
        if (data.hits) {
          // Sort by SMID descending (most recent first)
          const sorted = [...data.hits].sort((a, b) => b.SMID - a.SMID);
          setShadowMinistries(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch shadow ministries:', err);
        setError('Failed to load shadow ministries data');
      }
      setLoading(false);
    };
    fetchShadowMinistries();
  }, []);

  // User-sortable headers (#134); default order (SMID desc) is unchanged
  // until a header is clicked. Declared before any early return so the
  // Hook order is stable.
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    shadowMinistries,
    (m, col) => (SHADOW_SORT_KEYS[col] ? SHADOW_SORT_KEYS[col](m) : ''),
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isCurrent = (shadowMinistry) => {
    return !shadowMinistry.DateEnd || new Date(shadowMinistry.DateEnd) > new Date();
  };

  const calculateDuration = (start, end) => {
    if (!start) return null;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    if (years > 0) {
      return months > 0 ? `${years}y ${months}m` : `${years}y`;
    }
    return `${months}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  // Find current shadow ministry
  const currentShadowMinistry = shadowMinistries.find(m => isCurrent(m));

  const SortableTh = ({ col, sx }) => (
    <TableCell sx={{ fontWeight: 600, ...sx }} sortDirection={orderBy === col ? order : false}>
      {SHADOW_SORT_KEYS[col] ? (
        <TableSortLabel
          active={orderBy === col}
          direction={orderBy === col ? order : 'asc'}
          onClick={() => handleSort(col)}
        >
          {col}
        </TableSortLabel>
      ) : col}
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <GroupsIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
        <Box>
          <Typography variant="h4">Shadow Ministries</Typography>
          <Typography variant="body2" color="text.secondary">
            Australian Federal Opposition Shadow Ministry appointments
          </Typography>
        </Box>
      </Box>

      {/* Current Shadow Ministry Highlight */}
      {currentShadowMinistry && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
            border: '1px solid',
            borderColor: alpha(theme.palette.secondary.main, 0.2)
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Current Shadow Ministry" size="small" color="secondary" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.dark', mb: 0.5 }}>
                {currentShadowMinistry.Name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Appointed {formatDate(currentShadowMinistry.DateStart)}
                {' • '}
                {calculateDuration(currentShadowMinistry.DateStart, null)} serving
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/shadowministries/${currentShadowMinistry.SMID}`}
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 2 }}
            >
              View Shadow Ministry
            </Button>
          </Box>
        </Paper>
      )}

      {/* Shadow Ministries List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Shadow Ministry Chronology
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {shadowMinistries.length} shadow ministries
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <SortableTh col="#" sx={{ width: '8%' }} />
                <SortableTh col="Shadow Ministry" sx={{ width: '35%' }} />
                <SortableTh col="Appointed" sx={{ width: '18%' }} />
                <SortableTh col="End Date" sx={{ width: '18%' }} />
                <SortableTh col="Duration" sx={{ width: '10%' }} />
                <TableCell sx={{ fontWeight: 600, width: '11%', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((shadowMinistry) => {
                const current = isCurrent(shadowMinistry);
                return (
                  <TableRow
                    key={shadowMinistry.SMID}
                    sx={{
                      bgcolor: current ? alpha(theme.palette.secondary.main, 0.05) : 'inherit',
                      '&:hover': { bgcolor: current ? alpha(theme.palette.secondary.main, 0.1) : 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {shadowMinistry.SMID}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/shadowministries/${shadowMinistry.SMID}`}
                          sx={{
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 600,
                            color: current ? 'secondary.dark' : 'primary.main',
                            textAlign: 'left'
                          }}
                        >
                          {shadowMinistry.Name}
                        </Button>
                        {current && (
                          <Chip label="Current" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(shadowMinistry.DateStart)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {current ? (
                          <Chip label="In office" size="small" variant="outlined" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ) : (
                          formatDate(shadowMinistry.DateEnd)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {calculateDuration(shadowMinistry.DateStart, shadowMinistry.DateEnd)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Button
                        component={RouterLink}
                        to={`/shadowministries/${shadowMinistry.SMID}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ borderRadius: 2 }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
